/**
 * VENDORED from tc-console/src/mdx/MDXContent.tsx — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * MDXContent.tsx — the one canonical MDX presentation layer.
 *
 * Every markdown/MDX page in the product renders through this component:
 * Guest Home, Learner View Home (vendored), and anywhere else the product
 * shows a document. Plain markdown needs nothing from the caller:
 *
 *   <MDXContent text={doc.article} />
 *
 * Hosts with in-place navigation (Home's corpus) pass onLink; hosts extending
 * the vocabulary pass a registry. Presentation never forks: this is the only
 * ReactMarkdown wiring for documents, and its look comes entirely from the
 * MUI theme (see mdxComponents.tsx).
 *
 * SECURITY CONTRACT (unchanged from the pipeline's existing model). Content is
 * parsed as markdown only: react-markdown WITHOUT rehype-raw, so raw HTML/JSX
 * in a source file is escaped, never mounted; URLs pass react-markdown's
 * defaultUrlTransform, which strips javascript:/data: and friends. Richer
 * components arrive through the fenced-block registry (plain-text in, React
 * out) — arbitrary MDX cannot bypass the component system, because no
 * arbitrary code path exists.
 */

import { useMemo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Alert, Box } from '@mui/material'
import { createMdxComponents, type MdxDensity } from './mdxComponents'
import { mergeRegistries, type MdxComponentRegistry } from './registry'
import MermaidBlock from './MermaidBlock'

const REMARK_PLUGINS = [remarkGfm]

/** The product-shipped fenced blocks. Deliberately tiny today — `notice` is
 *  the working exemplar of the registry pattern (```notice … ``` renders its
 *  body as a themed alert) and `mermaid` draws diagrams (see MermaidBlock —
 *  lazy, themed, sanitized). Callout, Card, Figure, Badge, Accordion, Video,
 *  H5P, xAPI and skills widgets land here as they are built, one entry each,
 *  with no change to MDXContent itself. See registry.ts for the contract. */
function defaultBlocks(): MdxComponentRegistry['blocks'] {
  return {
    notice: (code) => (
      <Alert severity="info" variant="outlined" sx={{ my: 2, '& .MuiAlert-message': { width: '100%' } }}>
        <MDXContent text={code} density="compact" />
      </Alert>
    ),
    mermaid: (code) => <MermaidBlock code={code} />,
  }
}

export default function MDXContent({
  text,
  density = 'article',
  onLink,
  registry,
  components,
}: {
  /** The raw markdown/MDX source. */
  text: string
  /** `article` = the document surface (default); `compact` = cards/tiles. */
  density?: MdxDensity
  /** In-place link handling; absent = real anchors (external in a new tab). */
  onLink?: (href: string) => void
  /** Host-supplied registry extensions, merged over the defaults. */
  registry?: MdxComponentRegistry
  /** One-off element overrides, merged last — the escape hatch, not the norm. */
  components?: Partial<Components>
}) {
  const merged = useMemo<Components>(() => {
    const reg = mergeRegistries({ blocks: defaultBlocks() }, registry, { elements: components })
    return createMdxComponents({
      density,
      onLink,
      renderBlock: (code, language) => reg.blocks?.[language]?.(code, language),
    })
  }, [density, onLink, registry, components])

  return (
    <Box sx={density === 'article' ? { maxWidth: '72ch' } : undefined}>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={merged}>
        {text}
      </ReactMarkdown>
    </Box>
  )
}
